import {AnimatePresence, motion} from 'framer-motion';

import {createPortal} from 'react-dom';

import {useBulkActionExecution} from '../../hooks/useBulkActionExecution';
import {useBulkActions} from '../../hooks/useBulkActions';
import {useIsMobileView} from '../../hooks/useIsMobileView';
import {selectIsBulkActionLoading} from '../../store/createEagGridStore';
import {useActionsDefinition} from '../../store/selectors/useActionsDefinition';
import {useEagGridSelector} from '../../store/useEagGridSelector';
import {useEagGridStore} from '../../store/useEagGridStore';
import type {ActionCallback} from '../../types/ActionCallback';
import type {RowActions, RowData} from '../../types/RowData';
import {BulkActionsBar} from '../BulkActionsBar/BulkActionsBar';

const BULK_BAR_ANIMATION = {
  initial: {height: 0, opacity: 0, overflow: 'hidden'},
  animate: {height: 'auto', opacity: 1, overflow: 'hidden'},
  exit: {height: 0, opacity: 0, overflow: 'hidden'},
  transition: {duration: 0.2, ease: 'easeInOut'} as const,
};

type BulkActionSectionProps = {
  selectedRowIds: Set<string>;
  selectedRowsActions: RowActions[];
  rows: RowData[];
  gridCode: string;
  actionCallback?: ActionCallback<readonly string[]>;
  refreshData: () => void;
  onDeselectAll: () => void;
};

export function BulkActionSection(props: BulkActionSectionProps) {
  const store = useEagGridStore();
  const actionsDefinition = useActionsDefinition();
  const isBulkActionLoading = useEagGridSelector(selectIsBulkActionLoading);
  const isMobile = useIsMobileView();

  const bulkActions = useBulkActions(actionsDefinition ?? {}, props.selectedRowsActions);

  const handleBulkAction = useBulkActionExecution({
    actionCallback: props.actionCallback,
    rows: props.rows,
    selectedRowIds: props.selectedRowIds,
    gridCode: props.gridCode,
    refreshData: props.refreshData,
  });

  if (isMobile) {
    // Portalled to document.body, no motion wrapper: motion transforms on an
    // ancestor create a new containing block and break position:fixed descendants.
    return props.selectedRowIds.size > 0
      ? createPortal(
          <BulkActionsBar
            variant="floating"
            selectedCount={props.selectedRowIds.size}
            bulkActions={bulkActions}
            onDeselectAll={props.onDeselectAll}
            onBulkAction={handleBulkAction}
            disableAllActions={store.getState().disableAllActions}
            isLoading={isBulkActionLoading}
          />,
          document.body
        )
      : null;
  }

  return (
    <AnimatePresence>
      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
      {props.selectedRowIds.size > 0 && (
        <motion.div
          initial={BULK_BAR_ANIMATION.initial}
          animate={BULK_BAR_ANIMATION.animate}
          exit={BULK_BAR_ANIMATION.exit}
          transition={BULK_BAR_ANIMATION.transition}
        >
          <BulkActionsBar
            variant="inline"
            selectedCount={props.selectedRowIds.size}
            bulkActions={bulkActions}
            onDeselectAll={props.onDeselectAll}
            onBulkAction={handleBulkAction}
            disableAllActions={store.getState().disableAllActions}
            isLoading={isBulkActionLoading}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
